/**
 * Session occupancy — "how full is each run", counted the cheap way.
 *
 * Always via an AGGREGATE query (`count()`), never by reading the
 * registrations themselves. That is the whole design constraint: a `count()`
 * costs one read per 1000 matched entries, so a 500-competitor competition
 * costs exactly what a 5-competitor one does. Listening to `registrations`
 * instead would cost N reads per viewer plus a push on every entry —
 * affordable for the single competition a user is actively inside (the
 * registration modal does exactly that, and should, because that is the
 * moment occupancy has to be exact), ruinous for a card list or for a public
 * landing page a social post can point thousands of people at.
 *
 * The price is that it cannot be live: Firestore has no onSnapshot for
 * count(). Callers refresh at moments a reader would notice — mount, and
 * coming back to the tab.
 */
import { collection, getCountFromServer, query, where } from "firebase/firestore";
import { db } from "../firebase";
// `sessionFreeSlots` — free places for THIS viewer, their own place handed
// back — is the shared package's: the server's `sessionIsFull` is the same
// rule, so the form and the callable cannot disagree on a full session.
import { sessionFreeSlots } from "@sendly/shared";

/**
 * Competitors currently occupying each of the given sessions.
 *
 * `role == "competitor"` mirrors the server's own occupancy check in
 * registerForCompetition — judges and organizers hold a registration but do
 * not take a slot, so counting them would show a run as fuller than it is.
 *
 * Best-effort per session: one that fails (rules, a missing index) is simply
 * absent from the result rather than failing the whole set.
 *
 * @param {string} gymId
 * @param {string} compId
 * @param {string[]} sessionIds
 * @returns {Promise<Record<string, number>>} sessionId → competitor count
 */
/**
 * Free slots at or below which occupancy is reported NO MATTER WHAT — even on
 * a competition whose organizer switched entry counts off.
 *
 * ONE constant, deliberately: it is both the "shout about it" threshold and
 * the "override the organizer" threshold. Two numbers would mean a hidden-mode
 * competition could print "4 szabad hely" in calm grey — a number appearing
 * out of nowhere while looking like it doesn't matter. It also widens the
 * amber band from the old `<= 3` to `<= 4` on competitions that show counts,
 * which is the intended side effect: the band that warns and the band that
 * overrides must be the same band.
 */
export const SCARCE_FREE_SLOTS = 5;


/**
 * Does this competition show session entry counts to competitors?
 *
 * Defaults to TRUE for anything that predates the setting, so no existing
 * competition changes behaviour when the field simply isn't there.
 */
export function showsRegistrationCounts(competition) {
  return competition?.settings?.showRegistrationCounts !== false;
}

/**
 * Does THIS viewer see the session entry counts?
 *
 * The setting exists to stop ENTRANTS from picking a session by the numbers —
 * it was never meant to blind the people running the competition. An organizer
 * or anyone holding `competitions.manage` (gym admin, and via
 * `platform.gym.adminAny` a platform admin too) keeps seeing the real figures
 * on every surface, including the ones they share with competitors: the
 * competition card is where an organizer actually watches their sessions fill,
 * and sending them to the admin panel to read a number they are looking
 * straight at is not a workflow.
 *
 * Being a competitor in the same competition changes nothing — staff is staff.
 *
 * Deliberately NOT applied to the public register landing: that page is the
 * public face of the competition, and an organizer opening it should see
 * exactly what a visitor sees.
 *
 * @param {object}  competition
 * @param {boolean} isStaff  organizer of this competition, or `competitions.manage`
 */
export function seesRegistrationCounts(competition, isStaff) {
  return !!isStaff || showsRegistrationCounts(competition);
}

/**
 * What the right-hand side of a session row says about occupancy — the single
 * source of truth for the competition card, the public register landing and
 * the registration form's session step.
 *
 * Returns an i18n KEY plus params rather than a finished string, so the rule
 * stays testable without a `t`, and a tone name rather than a colour, so the
 * caller can resolve it against its own theme (`occupancyToneColor`).
 *
 * The capacity itself is NOT part of this: it lives on the session's own
 * descriptor (`<SessionCapacity>`), permanently, precisely so that "Megtelt"
 * cannot displace it. Losing the cap at the moment it matters most — you just
 * found out you can't get in, but not what you couldn't get into — is the bug
 * this split exists to prevent.
 *
 * `taken === undefined` means "not counted", not "empty": occupancy is only
 * counted while entries are open (see `useCompCardDetails` and the landing's
 * `regOpen` gate). A closed run therefore says nothing here, and nothing is
 * displaced — the capacity on the left keeps standing on its own.
 *
 * @param {object}  o
 * @param {number}  o.max         `session.maxParticipants` — 0/absent = uncapped
 * @param {number=} o.taken       competitors in the session, or undefined if not counted
 * @param {boolean} o.showCounts  may THIS viewer see the numbers — normally
 *   `seesRegistrationCounts(competition, isStaff)`; the public landing passes
 *   the bare `showsRegistrationCounts(competition)` on purpose
 * @param {boolean=} o.ownSlot    the viewer already holds a place here (the
 *   registration form's `keepSessionId`) — their own place is handed back to
 *   them, so a session that is full only because of them is not full for them
 * @returns {{key: string, params: object|null, tone: 'full'|'low'|'muted'}|null}
 */
export function getSessionOccupancy({ max, taken, showCounts, ownSlot = false }) {
  if (!(max > 0) || typeof taken !== "number") return null;
  const free = sessionFreeSlots({ max, taken, ownSlot });
  if (free <= 0) return { key: "comp.sessionFull", params: null, tone: "full" };
  // Both exceptions the organizer's switch cannot silence. Hiding "full" makes
  // people fill in a whole form the server will reject; hiding "nearly full"
  // makes them miss a place they would have taken had they known to hurry.
  if (free < SCARCE_FREE_SLOTS) return { key: "compReg.sessionFree", params: { free }, tone: "low" };
  if (!showCounts) return null;
  return { key: "compReg.sessionFree", params: { free }, tone: "muted" };
}

export async function countCompetitorsPerSession(gymId, compId, sessionIds) {
  if (!gymId || !compId || !sessionIds?.length) return {};
  const regs = collection(db, `gyms/${gymId}/competitions/${compId}/registrations`);
  const counted = await Promise.all(sessionIds.map(async (id) => {
    try {
      const agg = await getCountFromServer(
        query(regs, where("sessionId", "==", id), where("role", "==", "competitor")),
      );
      return [id, agg.data().count];
    } catch { return null; }
  }));
  return Object.fromEntries(counted.filter(Boolean));
}
